import { useState, useMemo } from "react";

// ─── VENDOR REGISTRY ───
const VENDORS = [
  { id: "ukg_pro", name: "UKG Pro", color: "#4d8c3c", abbr: "UKG", tier: "enterprise" },
  { id: "workday", name: "Workday HCM", color: "#1d4ed8", abbr: "WD", tier: "enterprise" },
  { id: "adp_gv", name: "ADP GlobalView", color: "#c41e2a", abbr: "ADP", tier: "enterprise" },
  { id: "dayforce", name: "Dayforce", color: "#6d28d9", abbr: "DF", tier: "enterprise" },
  { id: "bamboo", name: "BambooHR", color: "#73c41d", abbr: "BB", tier: "mid" },
  { id: "gusto", name: "Gusto", color: "#f97316", abbr: "GU", tier: "smb" },
  { id: "paycom", name: "Paycom", color: "#0d9488", abbr: "PC", tier: "mid" },
  { id: "paylocity", name: "Paylocity", color: "#0284c7", abbr: "PL", tier: "mid" },
  { id: "rippling", name: "Rippling", color: "#fbbf24", abbr: "RP", tier: "mid" },
];

const STATUS = {
  native:    { label: "Native",    icon: "●", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", weight: 3 },
  addon:     { label: "Add-on",    icon: "◐", color: "#b5791e", bg: "#faf4e8", border: "#fde68a", weight: 2 },
  partner:   { label: "Partner",   icon: "◌", color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe", weight: 1 },
  limited:   { label: "Limited",   icon: "◔", color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb", weight: 1 },
  none:      { label: "None",      icon: "○", color: "#c0334e", bg: "#fbeef0", border: "#fecaca", weight: 0 },
  different: { label: "Different", icon: "◆", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc", weight: 2 },
};

// ─── CAPABILITY DATA (25 functions × 9 vendors) ───
const AREAS = [
  { id: "core_hr", name: "Core HR", icon: "👤", functions: [
    { id: "employee_records", name: "Employee Records & Lifecycle",
      ukg_pro: { status: "native", notes: "Full lifecycle. Person→Employment split. Multi-company. SQL-based.", arch: "Person→Employment split. Configurable UDFs. Single DB for HR/payroll/benefits." },
      workday: { status: "native", notes: "Worker-centric unified model. Effective-dated history. Employees + contingent workers.", arch: "Worker object core entity. All transactions effective-dated. Position or job-based staffing." },
      adp_gv: { status: "native", notes: "SAP-based worker records. Multi-country with country-specific data models. GlobalID for cross-country worker identity.", arch: "SAP HR module. Country-specific infotypes. GlobalID unifies worker across countries." },
      dayforce: { status: "native", notes: "Unified single-record. Real-time data. Cloud-native single application.", arch: "One record for HR/payroll/time/benefits/talent. Single rules engine. No batch transfers." },
      bamboo: { status: "native", notes: "Clean, intuitive records. Self-service. Strong mid-market. Custom fields/tabs.", arch: "Simple employee model. Custom fields and tables. Employee self-service." },
      gusto: { status: "native", notes: "Simple records for SMBs. Easy setup. Limited custom fields.", arch: "Flat employee record. Minimal config. Optimized for simplicity." },
      paycom: { status: "native", notes: "Single-database. Self-service focused. Beti® employee-driven payroll.", arch: "Single DB. Employee self-service for data changes." },
      paylocity: { status: "native", notes: "Modern UI. Community features. Custom fields. Growing enterprise capabilities.", arch: "Employee record with social features. API-first." },
      rippling: { status: "native", notes: "Unified across HR + IT + Finance. Device management tied to lifecycle.", arch: "Employee spans HR+IT+Finance. Trigger-based automations. Device provisioning." },
    },
    { id: "position_mgmt", name: "Position Management",
      ukg_pro: { status: "native", notes: "Position management in Core HR. Tracks positions, incumbents, FTE.", arch: "Position linked to employment. Job code + position hierarchy." },
      workday: { status: "native", notes: "Core to data model — every worker fills a position. Headcount budgeting.", arch: "Position is first-class object with own lifecycle. Headcount groups." },
      adp_gv: { status: "native", notes: "SAP-based position management with organizational management (OM) module. Position hierarchy, reporting relationships.", arch: "SAP OM module. Position objects with relationships. Headcount planning via SAP integration." },
      dayforce: { status: "native", notes: "Position management with configurable org hierarchy and budgeting.", arch: "Position-based model with org unit assignment." },
      bamboo: { status: "limited", notes: "Basic job info tracking. No dedicated position module.", arch: "Job title/department on employee record." },
      gusto: { status: "none", notes: "No position management. Job titles only.", arch: "Flat job title field." },
      paycom: { status: "native", notes: "Position management with org chart. Budget tracking.", arch: "Position-based with department hierarchy." },
      paylocity: { status: "limited", notes: "Basic position tracking. Org chart. Limited budgeting.", arch: "Position fields on employee record." },
      rippling: { status: "limited", notes: "Basic job/department structure. No advanced position mgmt.", arch: "Department/team hierarchy." },
    },
    { id: "org_structure", name: "Organization Structure",
      ukg_pro: { status: "native", notes: "Fixed 4-level org hierarchy (orgLevel1-4). GL segment mapping.", arch: "4 fixed org levels. Multi-company/multi-EIN." },
      workday: { status: "native", notes: "Unlimited supervisory org with matrix support. Most flexible modeling.", arch: "Supervisory Org primary. Cost Center, Company, Region dimensions." },
      adp_gv: { status: "native", notes: "SAP organizational management — enterprise object model. Company code → personnel area → personnel subarea hierarchy per country.", arch: "SAP OM. Enterprise structure per country. Company codes. Personnel areas/subareas. Cost center integration." },
      dayforce: { status: "native", notes: "Configurable org levels. Strong labor allocation.", arch: "Configurable org tree. Drives security/reporting/scheduling." },
      bamboo: { status: "native", notes: "Department and location hierarchy. Visual org chart.", arch: "Department→Division. Visual org chart." },
      gusto: { status: "limited", notes: "Basic department structure only.", arch: "Flat department list." },
      paycom: { status: "native", notes: "Configurable org levels. Cost center mapping.", arch: "Multi-level org. Department codes. GL mapping." },
      paylocity: { status: "native", notes: "Org chart with configurable hierarchy.", arch: "Configurable org levels. Visual org chart." },
      rippling: { status: "native", notes: "Department/team hierarchy with IT/device policy groups.", arch: "Department→Team. Policy groups for IT and HR." },
    },
    { id: "credential_tracking", name: "Credential & License Tracking",
      ukg_pro: { status: "native", notes: "Skills/certs module. Licenses, expiration dates, alerts. Healthcare strong.", arch: "Credential records on profile. Alert engine for expirations." },
      workday: { status: "different", notes: "Via Skills Cloud and Qualifications — not dedicated credential table.", arch: "Skills & Qualifications→Certification type. ML ontology." },
      adp_gv: { status: "limited", notes: "Basic qualifications tracking via SAP Personnel Development. Not a dedicated credential module.", arch: "SAP PD qualifications catalog. Basic cert/license fields." },
      dayforce: { status: "native", notes: "Via Learning module. Scheduling considers credentials for compliance.", arch: "Certifications in Learning. Auto-alerts. Scheduling validates certs." },
      bamboo: { status: "limited", notes: "Training tracking module. Basic cert fields. No auto-alerts.", arch: "Training records table. Manual tracking." },
      gusto: { status: "none", notes: "No credential tracking.", arch: "N/A" },
      paycom: { status: "native", notes: "Certification tracking with expiration alerts. Ties to LMS.", arch: "Cert records with alerts. Connected to LMS." },
      paylocity: { status: "limited", notes: "Basic training/cert tracking. Limited automation.", arch: "Training records on profile." },
      rippling: { status: "partner", notes: "No native tracking. Marketplace integrations.", arch: "Third-party via App Shop." },
    },
    { id: "onboarding", name: "Onboarding",
      ukg_pro: { status: "native", notes: "Task workflows, document collection, I-9/E-Verify. New hire portal.", arch: "Configurable workflow. Tasks, documents, e-signatures." },
      workday: { status: "native", notes: "Configurable workflows, task assignment, pre-boarding.", arch: "Business process-driven. Connected to Recruiting and Core HR." },
      adp_gv: { status: "limited", notes: "Basic onboarding workflows. Not a strength — most clients use third-party or ADP Streamline for onboarding.", arch: "Limited onboarding forms. Country-specific new hire documentation. Often supplemented by third-party." },
      dayforce: { status: "native", notes: "Unified. Pre-boarding, tasks. Flows into HR record and payroll.", arch: "Single-record — data entered once, flows to all modules." },
      bamboo: { status: "native", notes: "Clean workflows. Pre-boarding emails, tasks, documents. Signatures.", arch: "Task-based. Email templates. Document upload. E-signatures." },
      gusto: { status: "native", notes: "Simple onboarding. Offer letters, documents, self-service setup.", arch: "Guided new hire flow. Self-service tax/deposit setup." },
      paycom: { status: "native", notes: "Task workflows, document management, I-9/E-Verify.", arch: "Onboarding workflow. Documents. Compliance forms." },
      paylocity: { status: "native", notes: "Modern experience. Task workflows, documents, compliance forms.", arch: "Configurable workflows. Mobile-friendly." },
      rippling: { status: "native", notes: "Best-in-class — HR+IT combined. Device shipping, app provisioning, docs in one flow.", arch: "Unified HR+IT onboarding. Device, SaaS, docs, payroll in single workflow." },
    },
  ]},
  { id: "payroll", name: "Payroll", icon: "💰", functions: [
    { id: "pay_processing", name: "Payroll Processing",
      ukg_pro: { status: "native", notes: "Full US/Canada. Multi-company/frequency. On-demand pay, earned wage access.", arch: "Pay group→earning/deduction→tax→register. Batch with preview." },
      workday: { status: "native", notes: "US + global (200+ countries via partners). Unlimited pay groups. Retro native.", arch: "Pay component architecture. Cloud Connect for third-party payroll." },
      adp_gv: { status: "native", notes: "Core strength — multi-country payroll in 40+ countries. Country-specific statutory payroll engines. ADP manages local compliance per country.", arch: "Country-specific payroll engines on SAP platform. Local statutory calculations. ADP experts manage country updates. Gross-to-net per country rules." },
      dayforce: { status: "native", notes: "Continuous calculation — key differentiator. Real-time updates. No batch. Dayforce Wallet.", arch: "Continuous gross-to-net. Single rules engine. No batch sync." },
      bamboo: { status: "partner", notes: "No native payroll. Partners with TRAXPayroll or third-party providers.", arch: "API integration with external payroll." },
      gusto: { status: "native", notes: "Full-service for SMBs. Automatic tax filing. Simple and intuitive.", arch: "Automated payroll run. Tax filing included. Direct deposit." },
      paycom: { status: "native", notes: "Full payroll with Beti® — employees verify/submit own payroll.", arch: "Employee-driven via Beti. Single database. Tax filing included." },
      paylocity: { status: "native", notes: "Full processing. On-demand pay. Multi-state/multi-EIN.", arch: "Payroll engine with on-demand pay. Tax filing. GL integration." },
      rippling: { status: "native", notes: "Full payroll with global. Auto state tax registration. Modern platform.", arch: "Automated with tax registration. Multi-state. Global 50+ countries." },
    },
    { id: "tax_management", name: "Tax Management & Filing",
      ukg_pro: { status: "native", notes: "Full federal/state/local. Smart Tax Search. Auto tax table updates.", arch: "Jurisdiction-based rules. W-2/1099 generation." },
      workday: { status: "native", notes: "Strong US tax + multi-country. Tax authority-based withholding.", arch: "Tax authority→withholding order. International via Cloud Connect." },
      adp_gv: { status: "native", notes: "Country-specific statutory tax engines for 40+ countries. ADP maintains local tax compliance. Far broader than US-only vendors.", arch: "Per-country tax calculation. ADP tax experts maintain statutory updates. Social security, income tax, local levies per country." },
      dayforce: { status: "native", notes: "Full tax with continuous processing. Multi-jurisdiction US/Canada.", arch: "Real-time tax recalc. Tax filing service." },
      bamboo: { status: "none", notes: "No native tax management. Handled by payroll partner.", arch: "N/A" },
      gusto: { status: "native", notes: "Full tax filing included. Federal, state, local. Auto payments.", arch: "Automated tax calc and filing. W-2. 1099." },
      paycom: { status: "native", notes: "Full tax management and filing. All jurisdictions.", arch: "Tax engine with all US jurisdictions. W-2/1099." },
      paylocity: { status: "native", notes: "Tax management and filing included. Multi-jurisdiction.", arch: "Tax calculation and filing. Multi-state." },
      rippling: { status: "native", notes: "Full tax filing. Auto state registration when employees move.", arch: "Automated registration. Files all jurisdictions. Dynamic state detection." },
    },
    { id: "garnishments", name: "Garnishment Processing",
      ukg_pro: { status: "native", notes: "Via Payment Services. Priority ordering, calculation, disbursement.", arch: "Garnishment service. Priority per federal/state rules." },
      workday: { status: "native", notes: "In Payroll module. Court order tracking, priority, auto calculations.", arch: "Garnishment as deduction type. Priority rules. Court order mgmt." },
      adp_gv: { status: "native", notes: "Country-specific garnishment/attachment processing. Rules vary by country — ADP handles local statutory requirements.", arch: "Garnishment processing per country rules. Court order management. Priority rules vary by jurisdiction." },
      dayforce: { status: "native", notes: "Included. Continuous calculation applies to garnishments.", arch: "Garnishment rules in continuous calc. Priority. Auto payment." },
      bamboo: { status: "none", notes: "No garnishment processing.", arch: "N/A" },
      gusto: { status: "native", notes: "Basic garnishment support. Court-ordered deductions.", arch: "Garnishment deductions. Priority ordering." },
      paycom: { status: "native", notes: "Garnishment processing with priority and disbursement.", arch: "Garnishment module. Priority. Payment processing." },
      paylocity: { status: "native", notes: "Garnishment management in payroll.", arch: "Garnishment deductions. Priority rules." },
      rippling: { status: "native", notes: "Garnishment processing in payroll.", arch: "Garnishment deductions with auto calculations." },
    },
    { id: "shift_differentials", name: "Shift Differentials & Premiums",
      ukg_pro: { status: "native", notes: "Earning codes for premiums. Flat dollar or %. Weighted average OT.", arch: "Earning code differentials. Pay rules. Multiple types." },
      workday: { status: "native", notes: "Compensation components linked to schedule/shift.", arch: "Differential as comp element. Flat, %, or formula." },
      adp_gv: { status: "native", notes: "Shift premiums via SAP wage types. Country-specific premium rules.", arch: "SAP wage types for differentials. Country-specific overtime and premium rules." },
      dayforce: { status: "native", notes: "Premium policies with complex rules — scheduling heritage strength.", arch: "Cascading premium rules. Time-of-day + day-of-week + holiday." },
      bamboo: { status: "none", notes: "No shift differential management.", arch: "N/A" },
      gusto: { status: "limited", notes: "Basic earning types for differentials. Manual setup.", arch: "Custom earning types." },
      paycom: { status: "native", notes: "Shift differentials with time integration.", arch: "Differential earning codes. Time integration." },
      paylocity: { status: "native", notes: "Shift differentials configurable in payroll.", arch: "Earning code differentials." },
      rippling: { status: "limited", notes: "Basic via custom earning types.", arch: "Custom earning types." },
    },
    { id: "w2_yearend", name: "W-2 / Year-End Processing",
      ukg_pro: { status: "native", notes: "W-2 generation, electronic/print, corrections. Self-service viewing.", arch: "Year-end module. W-2/W-3. Electronic consent." },
      workday: { status: "native", notes: "Year-end and W-2 included. Electronic distribution.", arch: "W-2 in payroll. Electronic via self-service." },
      adp_gv: { status: "native", notes: "Country-specific year-end processing. W-2 for US, P60 for UK, etc. ADP handles local statutory year-end per country.", arch: "Per-country year-end. Statutory forms per jurisdiction. ADP manages filing requirements." },
      dayforce: { status: "native", notes: "Year-end and W-2 included. Electronic distribution.", arch: "W-2 within payroll. Electronic consent." },
      bamboo: { status: "none", notes: "No W-2 processing. Via payroll partner.", arch: "N/A" },
      gusto: { status: "native", notes: "W-2 and 1099 generation/filing included.", arch: "Automated year-end. W-2/1099 generation." },
      paycom: { status: "native", notes: "Full year-end. W-2 generation and distribution.", arch: "Year-end module. W-2/W-3." },
      paylocity: { status: "native", notes: "W-2 processing and distribution included.", arch: "Year-end. W-2. Self-service." },
      rippling: { status: "native", notes: "W-2 and 1099 generation. Electronic distribution.", arch: "Automated year-end. W-2/1099." },
    },
  ]},
  { id: "time_wfm", name: "Time & Workforce Mgmt", icon: "⏱", functions: [
    { id: "time_tracking", name: "Time & Attendance",
      ukg_pro: { status: "addon", notes: "UKG WFM (Kronos heritage) separate product. Enterprise-grade. InTouch terminals.", arch: "Separate WFM DB. InTouch clocks, web/mobile. API sync to Pro." },
      workday: { status: "native", notes: "Included in HCM. Web, mobile, clocks. Same database.", arch: "Unified data model. Time entries→payroll directly." },
      adp_gv: { status: "addon", notes: "Time management via SAP CATS or third-party integration. Not a core GlobalView strength.", arch: "SAP CATS for time entry. Integration with third-party time clocks. Country-specific absence/attendance rules." },
      dayforce: { status: "native", notes: "Core strength — punches instantly update net pay via continuous calc.", arch: "Punch→instant recalculation. Web, mobile, kiosk, biometric." },
      bamboo: { status: "native", notes: "Basic clock-in/out. Timesheet management.", arch: "Timesheet-based. Manager approval." },
      gusto: { status: "native", notes: "Simple time tracking included. Clock in/out.", arch: "Basic time tracking. Timesheet→payroll." },
      paycom: { status: "native", notes: "Multiple clock options. Physical, web, mobile.", arch: "Native time. Clock hardware. Integrated with payroll." },
      paylocity: { status: "native", notes: "Web clock, mobile, physical clocks.", arch: "Time module with clocks. Payroll integration." },
      rippling: { status: "native", notes: "Geofencing, QR codes. Project time tracking.", arch: "Multiple methods. Project tracking. Payroll integration." },
    },
    { id: "scheduling", name: "Employee Scheduling",
      ukg_pro: { status: "addon", notes: "UKG WFM — AI-powered, demand-driven. Separate product.", arch: "WFM scheduling with AI/ML. Demand drivers." },
      workday: { status: "native", notes: "Auto-generates schedules. Newer module, still maturing.", arch: "Labor demand forecasting. Less mature than Dayforce/UKG." },
      adp_gv: { status: "partner", notes: "No native scheduling. Clients use third-party WFM solutions (Kronos, WorkForce Software) integrated via interface.", arch: "Third-party scheduling via file/API interface. No native demand-driven scheduling." },
      dayforce: { status: "native", notes: "Best-in-class — AI demand-driven. Qualifications, union rules, budget.", arch: "Demand forecasting. Constraint-based auto-scheduling." },
      bamboo: { status: "none", notes: "No scheduling.", arch: "N/A" },
      gusto: { status: "none", notes: "No scheduling.", arch: "N/A" },
      paycom: { status: "native", notes: "Shift management. Manager/employee self-service.", arch: "Schedule builder. Shift swap." },
      paylocity: { status: "addon", notes: "Scheduling add-on. Shift management.", arch: "Scheduling module. Templates." },
      rippling: { status: "limited", notes: "Basic scheduling. Not core strength.", arch: "Simple shift scheduling." },
    },
    { id: "absence_accruals", name: "Absence & Accrual Management",
      ukg_pro: { status: "native", notes: "Accrual plans in Pro. Separate engine in WFM. Complex carryover/caps.", arch: "Pro-side accrual + WFM engine. Two systems must sync." },
      workday: { status: "native", notes: "Absence Management in HCM. Complex rules, FMLA/ADA.", arch: "Absence plan→accrual calc. Leave cases with FMLA." },
      adp_gv: { status: "native", notes: "Country-specific absence management via SAP. Leave types, accruals, and entitlements per country statutory requirements.", arch: "SAP absence/attendance. Country-specific leave types and statutory entitlements. Quota management." },
      dayforce: { status: "native", notes: "Real-time accrual — updates continuously as time entered.", arch: "Continuous accrual engine. Complex carryover/caps." },
      bamboo: { status: "native", notes: "Time-off management with accruals. Clean UI.", arch: "Accrual policies. Request/approval. Balance tracking." },
      gusto: { status: "native", notes: "PTO policies with accruals. Simple and effective.", arch: "PTO config. Request/approval. Balance tracking." },
      paycom: { status: "native", notes: "Accrual management with complex policies.", arch: "Accrual engine. Multiple policy types." },
      paylocity: { status: "native", notes: "Time-off management with accruals.", arch: "Accrual policies. Request management." },
      rippling: { status: "native", notes: "PTO policies with accrual. Auto policy assignment by dept.", arch: "Policy-based accruals. Auto assignment." },
    },
    { id: "labor_forecasting", name: "Labor Forecasting & Optimization",
      ukg_pro: { status: "addon", notes: "WFM forecasting with AI/ML. Historical data, demand drivers.", arch: "WFM forecasting. Demand drivers by location." },
      workday: { status: "native", notes: "Labor optimization in WFM. Real-time cost tracking. Newer.", arch: "Budget tracking. Less mature than Dayforce/UKG WFM." },
      adp_gv: { status: "partner", notes: "No native labor forecasting. Requires third-party analytics or SAP BW integration.", arch: "Reporting via SAP BW or third-party. No native demand forecasting." },
      dayforce: { status: "native", notes: "Strong — sales, production, historical data. Integrated with scheduling.", arch: "Demand forecasting integrated with scheduling." },
      bamboo: { status: "none", notes: "No labor forecasting.", arch: "N/A" },
      gusto: { status: "none", notes: "No labor forecasting.", arch: "N/A" },
      paycom: { status: "limited", notes: "Basic labor cost reporting.", arch: "Labor cost reports." },
      paylocity: { status: "limited", notes: "Basic labor analytics.", arch: "Labor cost reporting." },
      rippling: { status: "none", notes: "No labor forecasting.", arch: "N/A" },
    },
  ]},
  { id: "benefits", name: "Benefits Administration", icon: "🏥", functions: [
    { id: "benefits_admin", name: "Benefits Enrollment & Admin",
      ukg_pro: { status: "native", notes: "Full benefits. Open enrollment, life events, plan comparison. Carrier via Boomi.", arch: "Benefit plan→deduction. Eligibility rules. Carrier feeds via Boomi." },
      workday: { status: "native", notes: "Comprehensive. Tile-based enrollment. Cloud Connect for Benefits.", arch: "Event-driven. Cloud Connect for carrier integrations." },
      adp_gv: { status: "different", notes: "Country-specific benefits — not a unified US-style benefits admin. Each country has its own statutory and supplemental benefit schemes.", arch: "Per-country benefits configuration. Statutory benefits handled automatically. Supplemental benefits vary by country. No unified enrollment portal like US-focused vendors." },
      dayforce: { status: "native", notes: "Decision support tools. Real-time deduction via continuous calc.", arch: "Decision support. Real-time deductions. Carrier integrations." },
      bamboo: { status: "addon", notes: "Benefits tracking. Limited carrier integrations.", arch: "Benefits tracking module." },
      gusto: { status: "native", notes: "Medical, dental, vision, 401k. Broker integration.", arch: "Integrated with carrier partnerships. Guided enrollment." },
      paycom: { status: "native", notes: "Enrollment, life events, carrier connections.", arch: "Benefits module. Carrier integrations." },
      paylocity: { status: "native", notes: "Modern enrollment. Carrier integrations.", arch: "Electronic carrier feeds. Self-service." },
      rippling: { status: "native", notes: "4,000+ plans. Automatic carrier sync. Broker-friendly.", arch: "4,000+ plans. Automated carrier data sync." },
    },
    { id: "cobra", name: "COBRA Administration",
      ukg_pro: { status: "addon", notes: "Via third-party partners.", arch: "Partner integration." },
      workday: { status: "different", notes: "COBRA as event type. Most use OSV for full admin.", arch: "Events tracked. Most outsource to OSV." },
      adp_gv: { status: "none", notes: "COBRA is US-specific. GlobalView clients with US population typically handle COBRA via ADP WFN or third-party.", arch: "N/A — US-specific regulation. Handled outside GlobalView." },
      dayforce: { status: "native", notes: "Included in Benefits. Qualifying events and notices.", arch: "COBRA triggered by status changes." },
      bamboo: { status: "none", notes: "No COBRA.", arch: "N/A" },
      gusto: { status: "addon", notes: "Via partner integration.", arch: "Partner-managed." },
      paycom: { status: "native", notes: "COBRA included. Qualifying events and notices.", arch: "COBRA module. Auto event detection." },
      paylocity: { status: "addon", notes: "Available as add-on.", arch: "COBRA service add-on." },
      rippling: { status: "native", notes: "Included. Automated qualifying event detection.", arch: "COBRA automation. Event-driven notices." },
    },
    { id: "aca_compliance", name: "ACA Compliance",
      ukg_pro: { status: "addon", notes: "ACA module add-on. Hours measurement, 1094/1095 generation.", arch: "Hours measurement. Forms generation." },
      workday: { status: "native", notes: "ACA dashboard in Benefits. Hours measurement, forms generation.", arch: "Integrated with Benefits and Time." },
      adp_gv: { status: "none", notes: "ACA is US-specific. GlobalView clients with US employees typically use ADP WFN or a dedicated US solution for ACA.", arch: "N/A — US-specific regulation." },
      dayforce: { status: "native", notes: "Built-in continuous hours measurement. Real-time tracking.", arch: "Continuous measurement via rules engine." },
      bamboo: { status: "none", notes: "No ACA tools.", arch: "N/A" },
      gusto: { status: "native", notes: "ACA reporting and 1095-C for applicable employers.", arch: "ACA reporting. 1095-C forms." },
      paycom: { status: "native", notes: "ACA with hours tracking and forms.", arch: "ACA module. 1094/1095 forms." },
      paylocity: { status: "native", notes: "ACA compliance tracking.", arch: "ACA tracking module." },
      rippling: { status: "native", notes: "ACA with auto tracking and 1095-C.", arch: "Automated tracking. Forms generation." },
    },
  ]},
  { id: "talent", name: "Talent Management", icon: "🌟", functions: [
    { id: "recruiting", name: "Recruiting & ATS",
      ukg_pro: { status: "native", notes: "ATS, requisitions, candidate management.", arch: "ATS with pipeline. Integrates with Onboarding." },
      workday: { status: "native", notes: "Strong module. Skills Cloud matching. AI talent rediscovery.", arch: "Skills-based matching. Collaborative hiring. CRM." },
      adp_gv: { status: "partner", notes: "No native ATS. Clients use third-party recruiting (Workday Recruiting, iCIMS, Greenhouse) integrated via API.", arch: "Third-party ATS integration. No native recruiting module." },
      dayforce: { status: "native", notes: "Pipeline→offer→onboarding flow.", arch: "Recruiting→employee record. Single flow." },
      bamboo: { status: "native", notes: "Built-in ATS. Job postings, candidate management.", arch: "Native ATS. Job board posting." },
      gusto: { status: "limited", notes: "Basic hiring tools. Job postings, offer letters.", arch: "Simple hiring flow." },
      paycom: { status: "native", notes: "ATS, candidate management, hiring workflows.", arch: "ATS module. Interview scheduling." },
      paylocity: { status: "native", notes: "Recruiting with ATS.", arch: "ATS with pipeline. Onboarding integration." },
      rippling: { status: "native", notes: "ATS, job postings, candidate management.", arch: "ATS. Job boards. Unified with onboarding." },
    },
    { id: "performance", name: "Performance Management",
      ukg_pro: { status: "native", notes: "Goals, reviews, 360, continuous feedback. Comp tie-in.", arch: "Workflows. Goal cascading. 360. Merit linkage." },
      workday: { status: "native", notes: "Goals, reviews, calibration, career dev, succession. Strong analytics.", arch: "Goals→Reviews→Calibration→Comp. Skills-based recommendations." },
      adp_gv: { status: "limited", notes: "Basic performance via SAP Personnel Development. Most clients use third-party talent management.", arch: "SAP PD appraisal module. Basic goal/review capability. Usually supplemented." },
      dayforce: { status: "native", notes: "Goals, reviews, feedback. Tied to compensation.", arch: "Goals. Reviews. Comp link." },
      bamboo: { status: "native", notes: "Goal tracking and reviews. Clean interface.", arch: "Goals. Reviews. Self-assessment." },
      gusto: { status: "limited", notes: "Basic reviews. Goal tracking. Growing.", arch: "Simple reviews. Basic goals." },
      paycom: { status: "native", notes: "Goals, reviews, 360 feedback.", arch: "Goal setting. 360 feedback." },
      paylocity: { status: "native", notes: "Modern review experience.", arch: "Goals. Reviews. Continuous feedback." },
      rippling: { status: "limited", notes: "Basic reviews and goals. Newer capability.", arch: "Review workflows. Goal setting." },
    },
    { id: "learning", name: "Learning Management (LMS)",
      ukg_pro: { status: "addon", notes: "UKG Pro Learning add-on. Compliance training.", arch: "LMS module. Separate subscription." },
      workday: { status: "native", notes: "In HCM. Skills Cloud recommendations.", arch: "LMS integrated. Learning recommendations." },
      adp_gv: { status: "partner", notes: "No native LMS. Clients use SuccessFactors Learning, Cornerstone, or other third-party LMS.", arch: "Third-party LMS integration." },
      dayforce: { status: "native", notes: "Embedded. Compliance certs with auto-renewal.", arch: "LMS integrated. Certification→credential chain." },
      bamboo: { status: "limited", notes: "Basic training tracking. Not full LMS.", arch: "Training records." },
      gusto: { status: "none", notes: "No LMS.", arch: "N/A" },
      paycom: { status: "native", notes: "LMS with course library and compliance.", arch: "LMS module. Compliance tracking." },
      paylocity: { status: "addon", notes: "Learning add-on.", arch: "LMS add-on." },
      rippling: { status: "partner", notes: "No native LMS. Marketplace integrations.", arch: "Third-party via App Shop." },
    },
    { id: "compensation", name: "Compensation Management",
      ukg_pro: { status: "native", notes: "Salary planning, merit cycles, bonus. Ties to performance.", arch: "Comp planning. Merit matrix. Budget tracking." },
      workday: { status: "native", notes: "Advanced — salary structures, merit, bonus, stock. Market data.", arch: "Comp plans. Benchmarking. Budget modeling. Statements." },
      adp_gv: { status: "different", notes: "Compensation managed per-country with local salary structures, statutory pay scales, and collective bargaining agreements.", arch: "Per-country comp structures. Collective agreement support. Local pay scales. Different from US-centric comp planning tools." },
      dayforce: { status: "native", notes: "Planning with merit and bonus. Performance integration.", arch: "Comp planning. Merit/bonus. Analytics." },
      bamboo: { status: "limited", notes: "Basic comp history. No planning module.", arch: "Comp history on record." },
      gusto: { status: "none", notes: "No comp management.", arch: "N/A" },
      paycom: { status: "native", notes: "Comp budgeting and planning. Merit cycles.", arch: "Comp planning. Budget tracking." },
      paylocity: { status: "native", notes: "Comp management with planning.", arch: "Comp planning. Merit cycles." },
      rippling: { status: "limited", notes: "Basic comp data. Pay bands. No advanced planning.", arch: "Comp data. Pay bands." },
    },
  ]},
  { id: "compliance", name: "Compliance & Analytics", icon: "📋", functions: [
    { id: "union_mgmt", name: "Union & Labor Relations",
      ukg_pro: { status: "native", notes: "Union tracking, dues deduction, seniority dates.", arch: "Union fields on employment. Dues deductions." },
      workday: { status: "limited", notes: "Basic union membership as attribute. Limited CBA rules.", arch: "Union as custom field. Less robust for seniority scheduling." },
      adp_gv: { status: "native", notes: "Strong — SAP handles works councils, collective bargaining agreements, and union rules per country. Critical for European operations.", arch: "Collective agreement integration. Works council data. Country-specific labor relations. Strong in Germany, France, Italy." },
      dayforce: { status: "native", notes: "Strong — seniority scheduling, dues, grievance. CBA enforcement.", arch: "Seniority rules in scheduling. OT distribution. CBA enforcement." },
      bamboo: { status: "none", notes: "No union management.", arch: "N/A" },
      gusto: { status: "none", notes: "No union management.", arch: "N/A" },
      paycom: { status: "limited", notes: "Basic union code and dues.", arch: "Union code. Dues deduction." },
      paylocity: { status: "limited", notes: "Basic union tracking.", arch: "Union fields. Dues." },
      rippling: { status: "none", notes: "No union management.", arch: "N/A" },
    },
    { id: "analytics", name: "Analytics & Reporting",
      ukg_pro: { status: "native", notes: "Pro BI (Cognos). Predictive analytics. Bryte AI for NL queries.", arch: "Cognos BI. Custom builder. Predictive. AI queries." },
      workday: { status: "native", notes: "Industry-leading. Prism Analytics. People Analytics. Illuminate AI.", arch: "Prism for external data. AI-driven insights. Discovery Boards." },
      adp_gv: { status: "addon", notes: "Reporting via SAP BW or ADP Analytics. Cross-country workforce reporting. GlobalView Portal dashboards.", arch: "SAP BW reporting. ADP GlobalView Portal. Cross-country analytics. Custom reports via SAP Query." },
      dayforce: { status: "native", notes: "Embedded analytics across modules. Real-time data.", arch: "Embedded. Real-time. Standard + custom reports." },
      bamboo: { status: "native", notes: "Standard reports, custom builder. Turnover analysis.", arch: "Report builder. Templates. CSV export." },
      gusto: { status: "limited", notes: "Basic reports. Payroll/tax. Limited custom.", arch: "Standard reports." },
      paycom: { status: "native", notes: "Custom report builder. Dashboards.", arch: "Report builder. Dashboards." },
      paylocity: { status: "native", notes: "Modern analytics. Custom reports. Dashboards.", arch: "Analytics platform. Interactive dashboards." },
      rippling: { status: "native", notes: "Custom report builder. Formula fields. Graphs.", arch: "Report builder with formulas. Graph builder." },
    },
    { id: "compliance_tools", name: "Regulatory Compliance Tools",
      ukg_pro: { status: "native", notes: "EEO, FLSA, new hire reporting, I-9. Auto regulatory updates.", arch: "Compliance dashboard. EEO/AAP. FLSA. I-9/E-Verify." },
      workday: { status: "native", notes: "Built-in with audit trails. EEO, FLSA. Global regulatory.", arch: "Configurable compliance. Audit trail on all transactions." },
      adp_gv: { status: "native", notes: "Core strength — statutory compliance across 40+ countries. ADP maintains country-specific regulatory updates. Strongest for multi-country compliance.", arch: "Per-country statutory compliance. ADP compliance teams maintain regulations. Social insurance, labor law, tax reporting per country." },
      dayforce: { status: "native", notes: "Real-time compliance. Clock attestation for meal breaks.", arch: "Rules engine enforces in real-time. Attestation surveys." },
      bamboo: { status: "limited", notes: "Basic EEO reporting.", arch: "EEO report." },
      gusto: { status: "native", notes: "Compliance alerts built in. New hire reporting. HR resources.", arch: "Compliance guidance. Auto new hire reporting." },
      paycom: { status: "native", notes: "EEO, FLSA, regulatory tracking.", arch: "Compliance module. Regulatory updates." },
      paylocity: { status: "native", notes: "Compliance tools. EEO, FLSA.", arch: "Compliance reporting." },
      rippling: { status: "native", notes: "Auto state registration. FLSA tracking.", arch: "Automated compliance. State registration." },
    },
  ]},
];

// ─── COMPONENTS ───

function StatusCell({ cap, isActive, onClick }) {
  if (!cap) return <div style={{ padding: "5px 7px", color: "#d1d5db", fontSize: 11 }}>—</div>;
  const s = STATUS[cap.status];
  return (
    <button onClick={onClick} style={{
      background: isActive ? s.bg : "transparent",
      border: `1.5px solid ${isActive ? s.border : "transparent"}`,
      borderRadius: 7, padding: "5px 7px", cursor: "pointer", display: "flex",
      alignItems: "center", gap: 5, width: "100%", textAlign: "left",
      transition: "all 0.12s", fontFamily: "inherit", outline: "none",
    }}
    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f9fafb"; }}
    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? s.bg : "transparent"; }}>
      <span style={{ color: s.color, fontSize: 13, lineHeight: 1 }}>{s.icon}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{s.label}</span>
    </button>
  );
}

function DetailPanel({ func, vendors, onClose }) {
  if (!func) return null;
  const frictionVendors = vendors.filter(v => {
    const cap = func[v.id];
    return cap && ["different","partner","limited","none","addon"].includes(cap.status);
  });
  const msg = {
    none: "Not available — requires workaround or third-party",
    partner: "Third-party integration — adds implementation effort",
    limited: "Basic only — gaps require workarounds",
    different: "Architecturally different — data mapping complexity",
    addon: "Separate purchase — budget and procurement impact",
  };
  return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 520, background: "white",
      boxShadow: "-12px 0 40px rgba(0,0,0,0.08)", zIndex: 100, display: "flex", flexDirection: "column",
      borderLeft: "1px solid #e5e7eb" }}>
      <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex",
        justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: -0.3 }}>{func.name}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>
            Vendor comparison · {vendors.length} vendors
          </div>
        </div>
        <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 6,
          width: 28, height: 28, cursor: "pointer", fontSize: 14, color: "#6b7280",
          display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}>
        {vendors.map(v => {
          const cap = func[v.id];
          if (!cap) return null;
          const s = STATUS[cap.status];
          return (
            <div key={v.id} style={{ marginBottom: 14, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "9px 14px", background: "#f9fafb", borderBottom: "1px solid #f3f4f6",
                display: "flex", alignItems: "center", gap: 9 }}>
                <div style={{ width: 24, height: 24, borderRadius: 5, background: v.color, color: "white",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8.5, fontWeight: 700 }}>
                  {v.abbr}
                </div>
                <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>{v.name}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px",
                  borderRadius: 4, background: s.bg, border: `1px solid ${s.border}` }}>
                  <span style={{ color: s.color, fontSize: 10 }}>{s.icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: s.color }}>{s.label}</span>
                </div>
              </div>
              <div style={{ padding: 12 }}>
                <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, marginBottom: 8 }}>{cap.notes}</div>
                {cap.arch && cap.arch !== "N/A" && (
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: 9 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: 0.5, color: "#9ca3af", marginBottom: 3 }}>Architecture</div>
                    <div style={{ fontSize: 11, color: "#4b5563", lineHeight: 1.5,
                      fontFamily: "var(--font-mono)" }}>{cap.arch}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {frictionVendors.length > 0 && (
          <div style={{ marginTop: 4, padding: 14, background: "#faf4e8", border: "1px solid #fde68a", borderRadius: 10 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: "#b5791e", marginBottom: 8 }}>⚠ Migration Friction</div>
            {frictionVendors.map(v => {
              const cap = func[v.id]; const s = STATUS[cap.status];
              return (
                <div key={v.id} style={{ marginBottom: 4, fontSize: 11.5, color: "#92400e", lineHeight: 1.5, display: "flex", gap: 6 }}>
                  <span style={{ fontWeight: 700, minWidth: 30, color: s.color }}>{v.abbr}</span>
                  <span>{msg[cap.status]}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreCard({ selectedVendors }) {
  const activeVendors = VENDORS.filter(v => selectedVendors.includes(v.id));
  const scores = {};
  selectedVendors.forEach(vid => {
    scores[vid] = { native: 0, addon: 0, different: 0, partner: 0, limited: 0, none: 0, total: 0 };
  });
  AREAS.forEach(a => a.functions.forEach(fn => {
    selectedVendors.forEach(vid => {
      const c = fn[vid]; if (c) { scores[vid][c.status]++; scores[vid].total++; }
    });
  }));

  const ranked = activeVendors.map(v => {
    const s = scores[v.id];
    return { ...v, score: s, pct: s.total > 0 ? Math.round(s.native / s.total * 100) : 0 };
  }).sort((a, b) => b.pct - a.pct);
  const bestId = ranked[0]?.id;

  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
      {activeVendors.map(v => {
        const s = scores[v.id];
        const pct = s.total > 0 ? Math.round(s.native / s.total * 100) : 0;
        const covPct = s.total > 0 ? Math.round((s.native + s.addon + s.different) / s.total * 100) : 0;
        const isBest = v.id === bestId;
        return (
          <div key={v.id} style={{
            flex: 1, minWidth: 170,
            border: `1px solid ${isBest ? v.color + "40" : "#e5e7eb"}`,
            borderRadius: 10, padding: 14,
            background: isBest ? v.color + "06" : "white", position: "relative",
          }}>
            {isBest && (
              <div style={{ position: "absolute", top: -1, right: 12, background: v.color,
                color: "white", fontSize: 8.5, fontWeight: 700, padding: "2px 8px",
                borderRadius: "0 0 5px 5px", letterSpacing: 0.3, textTransform: "uppercase" }}>
                Best Fit
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: v.color, color: "white",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 700 }}>
                {v.abbr}
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{v.name}</div>
                <div style={{ fontSize: 10.5, color: "#9ca3af" }}>{pct}% native · {covPct}% coverage</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 1.5, height: 6, borderRadius: 3, overflow: "hidden", background: "#f3f4f6" }}>
              {s.native > 0 && <div style={{ flex: s.native, background: "#059669" }} />}
              {s.addon > 0 && <div style={{ flex: s.addon, background: "#b5791e" }} />}
              {s.different > 0 && <div style={{ flex: s.different, background: "#0891b2" }} />}
              {s.partner > 0 && <div style={{ flex: s.partner, background: "#6366f1" }} />}
              {s.limited > 0 && <div style={{ flex: s.limited, background: "#9ca3af" }} />}
              {s.none > 0 && <div style={{ flex: s.none, background: "#c0334e" }} />}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: 10, color: "#6b7280" }}>
              <span><b style={{ color: "#059669" }}>{s.native}</b> native</span>
              <span><b style={{ color: "#b5791e" }}>{s.addon}</b> add-on</span>
              <span><b style={{ color: "#c0334e" }}>{s.none + s.partner + s.limited}</b> gaps</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GapsView({ vendors, onSelectFunc }) {
  const gaps = [];
  AREAS.forEach(area => {
    area.functions.forEach(fn => {
      const vendorGaps = vendors.filter(v => {
        const cap = fn[v.id];
        return cap && ["none", "limited", "partner"].includes(cap.status);
      });
      if (vendorGaps.length > 0) {
        gaps.push({ ...fn, area: area.name, areaIcon: area.icon, gapVendors: vendorGaps });
      }
    });
  });

  return (
    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: "#fbeef0", borderBottom: "1px solid #fecaca",
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#c0334e" }}>
          ⚠ {gaps.length} Capabilities with Gaps
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          Functions where at least one selected vendor has None, Limited, or Partner status
        </div>
      </div>
      {gaps.map(fn => (
        <div key={fn.id} onClick={() => onSelectFunc(fn)}
          style={{ padding: "10px 16px", borderBottom: "1px solid #f3f4f6", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 12, transition: "background 0.1s" }}
          onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          <span style={{ fontSize: 14 }}>{fn.areaIcon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827" }}>{fn.name}</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{fn.area}</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {fn.gapVendors.map(v => {
              const cap = fn[v.id]; const s = STATUS[cap.status];
              return (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 3,
                  padding: "2px 7px", borderRadius: 4, background: s.bg, border: `1px solid ${s.border}` }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: v.color, color: "white",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 6.5, fontWeight: 700 }}>
                    {v.abbr}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: s.color }}>{s.label}</span>
                </div>
              );
            })}
          </div>
          <span style={{ fontSize: 10, color: "#d1d5db" }}>→</span>
        </div>
      ))}
    </div>
  );
}

// ─── MAIN ───
export default function SchemaCompare({ embedded = false, customerName, customerSystems }) {
  // Auto-detect vendors from customer systems when embedded
  const autoVendors = useMemo(() => {
    if (!customerSystems?.length) return null;
    const vendorMap = {
      'ukg': 'ukg_pro', 'ukg pro': 'ukg_pro', 'ultipro': 'ukg_pro',
      'workday': 'workday',
      'adp': 'adp_gv', 'adp globalview': 'adp_gv', 'adp workforce now': 'adp_gv',
      'dayforce': 'dayforce', 'ceridian': 'dayforce',
      'bamboohr': 'bamboo', 'bamboo': 'bamboo',
      'gusto': 'gusto',
      'paycom': 'paycom',
      'paylocity': 'paylocity',
      'rippling': 'rippling',
    };
    const matched = [];
    for (const sys of customerSystems) {
      const name = (sys.vendor_name || sys.product_name || '').toLowerCase();
      for (const [key, vid] of Object.entries(vendorMap)) {
        if (name.includes(key) && !matched.includes(vid)) {
          matched.push(vid);
        }
      }
    }
    return matched.length >= 2 ? matched.slice(0, 5) : null;
  }, [customerSystems]);

  const [selectedVendors, setSelectedVendors] = useState(autoVendors || ["ukg_pro", "workday", "adp_gv", "dayforce"]);
  const [activeCell, setActiveCell] = useState(null);
  const [detailFunc, setDetailFunc] = useState(null);
  const [expandedAreas, setExpandedAreas] = useState(AREAS.map(a => a.id));
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("matrix");

  const toggleVendor = vid => setSelectedVendors(p =>
    p.includes(vid) ? (p.length > 2 ? p.filter(v => v !== vid) : p) : (p.length < 5 ? [...p, vid] : p)
  );
  const toggleArea = id => setExpandedAreas(p =>
    p.includes(id) ? p.filter(a => a !== id) : [...p, id]
  );
  const activeVendors = VENDORS.filter(v => selectedVendors.includes(v.id));
  const totalFunctions = AREAS.reduce((s, a) => s + a.functions.length, 0);

  const filteredAreas = useMemo(() => {
    if (!search) return AREAS;
    const q = search.toLowerCase();
    return AREAS.map(area => ({
      ...area,
      functions: area.functions.filter(fn =>
        fn.name.toLowerCase().includes(q) ||
        activeVendors.some(v => fn[v.id]?.notes?.toLowerCase().includes(q))
      ),
    })).filter(area => area.functions.length > 0);
  }, [search, selectedVendors]);

  return (
    <div style={{ fontFamily: "var(--font-sans)", background: embedded ? "transparent" : "#f8f9fa",
      minHeight: embedded ? "auto" : "100vh", color: "#111827", WebkitFontSmoothing: "antialiased" }}>

      {/* Header — hidden in embedded mode */}
      {!embedded && (
      <div style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "11px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: "#4d8c3c",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "white", fontSize: 10, fontWeight: 800 }}>X</span>
            </div>
            <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: -0.5 }}>
              Sempirical
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#9ca3af" }}>
            <span>{customerName || "Pinnacle Health System"}</span>
            <span style={{ color: "#d1d5db" }}>›</span>
            <span style={{ color: "#374151", fontWeight: 600 }}>Schema Compare</span>
            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>
              ({totalFunctions} capabilities · {AREAS.length} areas · {VENDORS.length} vendors)
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 6,
            border: "1px solid #e5e7eb", background: "white", color: "#6b7280",
            cursor: "pointer", fontFamily: "inherit" }}>Export Matrix</button>
          <button style={{ padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 6,
            border: "1px solid #4d8c3c", background: "#4d8c3c", color: "white",
            cursor: "pointer", fontFamily: "inherit" }}>Save to Work Effort</button>
        </div>
      </div>
      )}

      <div style={{ maxWidth: embedded ? "none" : 1400, margin: "0 auto", padding: embedded ? "16px 24px 40px" : "20px 24px 80px" }}>

        {/* Vendor chips */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase",
            letterSpacing: 0.5 }}>Compare:</span>
          {VENDORS.map(v => {
            const on = selectedVendors.includes(v.id);
            return (
              <button key={v.id} onClick={() => toggleVendor(v.id)} style={{
                padding: "4px 11px", borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                border: `1.5px solid ${on ? v.color : "#e5e7eb"}`,
                background: on ? `${v.color}10` : "white",
                color: on ? v.color : "#9ca3af",
                display: "flex", alignItems: "center", gap: 4,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: on ? v.color : "#d1d5db" }} />
                {v.name}
              </button>
            );
          })}
          <span style={{ fontSize: 10, color: "#d1d5db", marginLeft: 4 }}>
            {selectedVendors.length}/5 max
          </span>
        </div>

        <ScoreCard selectedVendors={selectedVendors} />

        {/* Controls row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          {/* Search */}
          <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search capabilities..."
              style={{ width: "100%", padding: "7px 12px 7px 30px", border: "1px solid #e5e7eb",
                borderRadius: 8, fontSize: 12, fontFamily: "inherit", outline: "none",
                background: "white", color: "#111827" }}
              onFocus={e => e.target.style.borderColor = "#4d8c3c"}
              onBlur={e => e.target.style.borderColor = "#e5e7eb"} />
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              fontSize: 12, color: "#9ca3af", pointerEvents: "none" }}>⌕</span>
            {search && (
              <button onClick={() => setSearch("")}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "#f3f4f6", border: "none", borderRadius: 4, width: 16, height: 16,
                  cursor: "pointer", fontSize: 9, color: "#6b7280",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            )}
          </div>

          {/* View toggle */}
          <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 7, padding: 2 }}>
            {[["matrix", "Matrix"], ["gaps", "Gaps Only"]].map(([mode, label]) => (
              <button key={mode} onClick={() => setViewMode(mode)} style={{
                padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 5,
                border: "none", cursor: "pointer", fontFamily: "inherit",
                background: viewMode === mode ? "white" : "transparent",
                color: viewMode === mode ? "#111827" : "#9ca3af",
                boxShadow: viewMode === mode ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}>{label}</button>
            ))}
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 10, marginLeft: "auto", flexWrap: "wrap" }}>
            {Object.entries(STATUS).map(([k, s]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, color: "#6b7280" }}>
                <span style={{ color: s.color, fontSize: 11 }}>{s.icon}</span>
                <span style={{ fontWeight: 500 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Gaps view */}
        {viewMode === "gaps" && (
          <GapsView vendors={activeVendors} onSelectFunc={fn => setDetailFunc(fn)} />
        )}

        {/* Matrix view */}
        {viewMode === "matrix" && (
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
            {/* Column headers */}
            <div style={{
              display: "grid",
              gridTemplateColumns: `250px repeat(${activeVendors.length}, 1fr)`,
              borderBottom: "2px solid #e5e7eb", background: "#f9fafb",
              position: embedded ? "relative" : "sticky", top: embedded ? "auto" : 44, zIndex: 10,
            }}>
              <div style={{ padding: "10px 16px", fontSize: 10, fontWeight: 600, color: "#9ca3af",
                textTransform: "uppercase", letterSpacing: 0.5 }}>Function</div>
              {activeVendors.map(v => (
                <div key={v.id} style={{ padding: "10px 10px", display: "flex", alignItems: "center",
                  gap: 6, borderLeft: "1px solid #e5e7eb" }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, background: v.color, color: "white",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 7.5, fontWeight: 700 }}>{v.abbr}</div>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{v.name}</span>
                </div>
              ))}
            </div>

            {/* Area sections */}
            {filteredAreas.map(area => {
              const exp = expandedAreas.includes(area.id);
              return (
                <div key={area.id}>
                  {/* Area header */}
                  <div onClick={() => toggleArea(area.id)} style={{
                    display: "grid",
                    gridTemplateColumns: `250px repeat(${activeVendors.length}, 1fr)`,
                    borderBottom: "1px solid #e5e7eb", cursor: "pointer", background: "#f9fafb",
                  }}>
                    <div style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 13 }}>{area.icon}</span>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>{area.name}</span>
                      <span style={{ fontSize: 10, color: "#9ca3af" }}>({area.functions.length})</span>
                      <span style={{ fontSize: 8, color: "#9ca3af", marginLeft: "auto" }}>{exp ? "▼" : "▶"}</span>
                    </div>
                    {activeVendors.map(v => {
                      const c = { native: 0, addon: 0, gap: 0 };
                      area.functions.forEach(fn => {
                        const cap = fn[v.id];
                        if (cap) {
                          if (cap.status === "native") c.native++;
                          else if (cap.status === "addon" || cap.status === "different") c.addon++;
                          else c.gap++;
                        }
                      });
                      return (
                        <div key={v.id} style={{ padding: "8px 10px", borderLeft: "1px solid #e5e7eb",
                          display: "flex", gap: 8, alignItems: "center", fontSize: 10, color: "#6b7280" }}>
                          <span style={{ color: "#059669" }}>●{c.native}</span>
                          {c.addon > 0 && <span style={{ color: "#b5791e" }}>◐{c.addon}</span>}
                          {c.gap > 0 && <span style={{ color: "#c0334e" }}>○{c.gap}</span>}
                        </div>
                      );
                    })}
                  </div>

                  {/* Function rows */}
                  {exp && area.functions.map(fn => (
                    <div key={fn.id} style={{
                      display: "grid",
                      gridTemplateColumns: `250px repeat(${activeVendors.length}, 1fr)`,
                      borderBottom: "1px solid #f3f4f6",
                      background: detailFunc?.id === fn.id ? "#f0fdf4" : "white",
                      transition: "background 0.1s",
                    }}>
                      <div style={{ padding: "7px 16px 7px 36px", fontSize: 12, color: "#374151",
                        cursor: "pointer", display: "flex", alignItems: "center" }}
                        onClick={() => setDetailFunc(detailFunc?.id === fn.id ? null : fn)}>
                        <span style={{ flex: 1 }}>{fn.name}</span>
                        <span style={{ fontSize: 8, color: "#d1d5db" }}>→</span>
                      </div>
                      {activeVendors.map(v => (
                        <div key={v.id} style={{ padding: "4px 6px", borderLeft: "1px solid #f3f4f6" }}>
                          <StatusCell cap={fn[v.id]}
                            isActive={activeCell === `${fn.id}-${v.id}`}
                            onClick={() => {
                              setActiveCell(activeCell === `${fn.id}-${v.id}` ? null : `${fn.id}-${v.id}`);
                              setDetailFunc(fn);
                            }} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 10.5, color: "#9ca3af", textAlign: "center" }}>
          Sempirical Schema Compare · {VENDORS.length} vendors · {totalFunctions} capabilities ·
          Data sourced from vendor documentation, G2, Gartner, vendor websites · Last verified Feb 2026
        </div>
      </div>

      {/* Detail panel */}
      {detailFunc && (
        <DetailPanel func={detailFunc} vendors={activeVendors}
          onClose={() => { setDetailFunc(null); setActiveCell(null); }} />
      )}
    </div>
  );
}
